import React from 'react';
import { MessageSquare, FileText } from 'lucide-react';

const ViewModeToggler = ({ toggleViewMode, viewMode }) => {
  return (
    <div className="flex justify-center mb-6 animate-slide-up">
      <div className="relative inline-flex items-center p-1.5 rounded-2xl shadow-2xl border bg-surface/60 border-text-muted/20">

        <div
          className={`absolute top-1.5 bottom-1.5 w-[calc(50%-6px)] rounded-xl transition-all duration-500 ease-out
            ${viewMode === 'single'
              ? 'left-1.5 bg-gradient-gold-ember shadow-gold'
              : 'left-[calc(50%+3px)] bg-gradient-teal-gold shadow-teal'
            }`}
        />

        {/* Unified View */}
        <button
          onClick={() => toggleViewMode('single')}
          className={`relative z-10 flex items-center gap-2.5 px-6 py-3.5 rounded-xl font-semibold transition-all duration-300 min-w-[140px] justify-center
            ${viewMode === 'single'
              ? 'scale-105 text-text-primary'
              : 'text-text-muted hover:text-text-inverse hover:scale-102'
            }`}
        >
          <div className="relative">
            <FileText
              className={`w-5 h-5 transition-all duration-300
                ${viewMode === 'single' ? 'drop-shadow-glow' : ''}`}
            />

            {viewMode === 'single' && (
              <>
                <div className="absolute inset-0 opacity-30 animate-ping">
                  <FileText className="w-5 h-5" />
                </div>
                <div className="absolute -inset-1 rounded-full blur-sm bg-text-inverse/25 animate-pulse" />
              </>
            )}
          </div>

          <span className="text-sm font-bold tracking-wide">
            Unified
          </span>

          {viewMode === 'single' && (
            <div className="absolute -top-1 -right-1 w-2 h-2 rounded-full animate-pulse border-2 border-text-inverse bg-accent-primary shadow-lg" />
          )}
        </button>

        {/* Divider */}
        <div className="relative z-10 w-px h-8 mx-1 bg-gradient-to-b from-transparent via-text-muted/25 to-transparent" />

        {/* Conversation View */}
        <button
          onClick={() => toggleViewMode('multiple')}
          className={`relative z-10 flex items-center gap-2.5 px-6 py-3.5 rounded-xl font-semibold transition-all duration-300 min-w-[140px] justify-center
            ${viewMode === 'multiple'
              ? 'scale-105 text-text-primary'
              : 'text-text-muted hover:text-text-inverse hover:scale-102'
            }`}
        >
          <div className="relative">
            <MessageSquare
              className={`w-5 h-5 transition-all duration-300
                ${viewMode === 'multiple' ? 'drop-shadow-glow' : ''}`}
            />

            {viewMode === 'multiple' && (
              <>
                <div className="absolute inset-0 opacity-30 animate-ping">
                  <MessageSquare className="w-5 h-5" />
                </div>
                <div className="absolute -inset-1 rounded-full blur-sm bg-text-inverse/25 animate-pulse" />
              </>
            )}
          </div>

          <span className="text-sm font-bold tracking-wide">
            Conversation
          </span>

          {viewMode === 'multiple' && (
            <div className="absolute -top-1 -right-1 w-2 h-2 rounded-full animate-pulse border-2 border-text-inverse bg-accent-tertiary shadow-lg" />
          )}
        </button>

        {/* Floating Particles */}
        <div className="absolute -inset-4 pointer-events-none">
          {[...Array(3)].map((_, i) => (
            <div
              key={i}
              className="absolute w-1 h-1 rounded-full animate-float bg-accent-primary/35"
              style={{
                left: `${20 + i * 30}%`,
                top: `${-10 + i * 5}px`,
                animationDelay: `${i * 0.5}s`,
                animationDuration: '3s',
              }}
            />
          ))}
        </div>
      </div>

      <style jsx>{`
        @keyframes float {
          0%, 100% {
            transform: translateY(0) scale(1);
            opacity: 0;
          }
          50% {
            transform: translateY(-20px) scale(1.5);
            opacity: 0.6;
          }
        }
        .hover\\:scale-102:hover {
          transform: scale(1.02);
        }
      `}</style>
    </div>
  );
};

export default ViewModeToggler;